import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Button, Paper } from '@mui/material';
import AUHeader from '../components/AUHeader';
import VideoAccessForm from '../components/VideoAccessForm';
import DescargaPDF from '../components/DescargaPDF';
import { hasValidVideoAccess, setStoredVideoAccess } from '../utils/videoAccessStorage';
import { submitLead } from '../utils/submitLead';

const BLUE = '#1A7FFF';
const BLUE_HOVER = '#1568D9';
const BLUE_SOFT = '#EBF3FF';
const DARK = '#0F1117';
const GRAY = '#64748B';
const BORDER = '#E2E8F0';
const GRAY_LIGHT = '#F8FAFC';

const PDF_URL = '/recursos/guia-sueno-au-coaching.pdf';
// Lista de MercadoLibre con los suplementos que usa y recomienda Agustín
const SUPP_URL = 'https://meli.la/25VYH58';

// ---- contenido de la guía ----
const HABITS = [
  {
    n: '1',
    title: 'Corta los estimulantes a tiempo',
    sub: 'cafeína y cabeza',
    items: [
      'Último café entre 6 y 8 horas antes de acostarte. La cafeína sigue circulando mucho más rato del que crees.',
      'El trabajo, los pendientes y las discusiones se cierran mínimo 1,5 horas antes de la cama. Tu cabeza necesita tiempo para bajar las revoluciones.',
      'Entrena con margen: si sales del gimnasio y te acuestas al tiro, el sistema nervioso sigue acelerado.',
    ],
  },
  {
    n: '2',
    title: 'Última comida con carbohidratos',
    sub: 'la que nadie hace bien',
    intro: 'No necesitas alejar la comida de la cama. Lo importante es qué lleva: tu última comida debe tener carbohidratos (arroz, papas, avena, pan), porque ayudan a conciliar el sueño. Acostarte con hambre es la mejor forma de dar vueltas en la cama.',
    tip: {
      title: 'Pro tip que nadie te dice: más sal',
      body: 'Si despiertas varias veces al baño en la noche: limita el agua en la noche (sorbos, no vasos) y agrégale más sal a tu última comida. Es un truco que usamos los que competimos para retener fluido y dormir de corrido. Pruébalo unos días y evalúa.',
    },
  },
  {
    n: '3',
    title: 'Tu pieza es una cueva',
    sub: 'ambiente',
    items: [
      'Fría: idealmente entre 19 y 21 °C. El cuerpo necesita bajar su temperatura para dormirse.',
      'Oscura total: cortinas blackout o antifaz. La oscuridad sostiene la melatonina que produces solo.',
      'La cama se usa para dormir. No para trabajar, no para ver series, no para el scroll.',
    ],
  },
  {
    n: '4',
    title: 'Pantallas fuera 60 minutos antes',
    sub: 'la última hora',
    items: [
      'Suelta el teléfono y el computador unos 60 minutos antes de acostarte.',
      'Si no puedes, lentes bloqueadores de luz azul o el filtro nocturno del teléfono pueden ayudar.',
      'Llena esa hora con cosas que te bajen: leer, ducha caliente, respirar, conversar. Nada de pelear con desconocidos en Instagram.',
    ],
  },
];

const SUPPLEMENTS = [
  { name: 'Magnesio', sub: 'Bisglicinato o L-treonato', dose: '1.000 mg antes de dormir', why: 'Relaja el sistema nervioso y los músculos. Ayuda a conciliar el sueño.' },
  { name: 'Zinc', sub: 'Mineral clave en la noche', dose: '30 mg antes de dormir', why: 'Apoya la recuperación y el sistema inmune mientras duermes.' },
  { name: 'Melatonina', sub: 'La señal de apagado', dose: '3 a 9 mg antes de dormir', why: 'La hormona que le avisa al cerebro que es hora de dormir. Ayuda a quedarte dormido.' },
];

const CHECKLIST = [
  'Último café a más de 6 horas de acostarme',
  'Trabajo y discusiones cerrados 1,5 horas antes',
  'Última comida con carbohidratos',
  'Agua a sorbos en la noche (y más sal si despierto al baño)',
  'Pieza fría (19-21 °C) y oscura total',
  'Pantallas fuera 60 minutos antes, o lentes de luz azul',
  'Última hora en bajada: leer, ducha caliente, respirar',
];

// ---- subcomponentes ----
function SectionTitle({ children }) {
  return (
    <Typography sx={{
      fontFamily: 'Kanit, sans-serif', fontWeight: 800, color: DARK,
      fontSize: { xs: '1.3rem', md: '1.6rem' }, mb: 2.5, mt: 1,
    }}>
      {children}
    </Typography>
  );
}

function HabitBlock({ habit }) {
  return (
    <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', border: `1px solid ${BORDER}`, mb: 2.5 }}>
      <Box sx={{
        bgcolor: BLUE_HOVER, px: { xs: 2, md: 2.5 }, py: 1.25,
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5,
      }}>
        <Typography sx={{ fontWeight: 800, color: '#fff', fontFamily: 'Kanit, sans-serif', fontSize: 16 }}>
          Hábito {habit.n} · {habit.title}
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: '#CCE5FF', fontFamily: 'Kanit, sans-serif' }}>
          {habit.sub}
        </Typography>
      </Box>
      <Box sx={{ px: { xs: 2, md: 2.5 }, py: 2 }}>
        {habit.intro && (
          <Typography sx={{ color: DARK, fontFamily: 'Kanit, sans-serif', fontSize: 14.5, mb: 1.75 }}>
            {habit.intro}
          </Typography>
        )}
        {habit.items && habit.items.map((it) => (
          <Box key={it} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 1.25 }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: BLUE, mt: 0.9, flexShrink: 0 }} />
            <Typography sx={{ color: DARK, fontFamily: 'Kanit, sans-serif', fontSize: 14.5 }}>{it}</Typography>
          </Box>
        ))}
        {habit.tip && (
          <Paper elevation={0} sx={{
            mt: 1.5, p: 2, borderRadius: 2, bgcolor: GRAY_LIGHT,
            border: `1px solid ${BORDER}`, borderLeft: '4px solid #FF8C00',
          }}>
            <Typography sx={{ fontWeight: 700, color: '#B35900', fontFamily: 'Kanit, sans-serif', fontSize: 13.5, mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.3 }}>
              {habit.tip.title}
            </Typography>
            <Typography sx={{ color: DARK, fontFamily: 'Kanit, sans-serif', fontSize: 14 }}>
              {habit.tip.body}
            </Typography>
          </Paper>
        )}
      </Box>
    </Paper>
  );
}

function SupplementCard({ s }) {
  return (
    <Paper elevation={0} sx={{
      flex: 1, p: 2.25, borderRadius: 3, bgcolor: '#fff',
      border: `1px solid ${BORDER}`, borderLeft: `4px solid ${BLUE}`,
    }}>
      <Typography sx={{ fontWeight: 800, color: BLUE_HOVER, fontFamily: 'Kanit, sans-serif', fontSize: 15, mb: 0.25 }}>
        {s.name}
      </Typography>
      <Typography sx={{ fontSize: 12, color: GRAY, fontFamily: 'Kanit, sans-serif', mb: 1 }}>
        {s.sub}
      </Typography>
      <Typography sx={{ fontWeight: 700, color: DARK, fontFamily: 'Kanit, sans-serif', fontSize: 14, mb: 1 }}>
        {s.dose}
      </Typography>
      <Typography sx={{ fontSize: 13, color: GRAY, fontFamily: 'Kanit, sans-serif' }}>
        {s.why}
      </Typography>
    </Paper>
  );
}

const SleepGuidePage = () => {
  const navigate = useNavigate();
  const [unlocked, setUnlocked] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);

  const handleUnlock = () => {
    if (hasValidVideoAccess()) {
      setUnlocked(true);
    } else {
      setShowLeadForm(true);
    }
  };

  const handleLeadSubmit = async (data) => {
    setShowLeadForm(false);
    setStoredVideoAccess(data);
    await submitLead({ data, source: 'sleep-guide' });
    setUnlocked(true);
  };

  return (
    <Box sx={{ bgcolor: GRAY_LIGHT, fontFamily: 'Kanit, sans-serif', minHeight: '100vh' }}>
      <AUHeader />

      <Container maxWidth="lg" sx={{ pt: { xs: 4, md: 6 }, pb: { xs: 6, md: 10 } }}>
        {/* Hero */}
        <Typography sx={{
          fontSize: 15, fontWeight: 700, letterSpacing: 2.5, color: BLUE,
          textTransform: 'uppercase', mb: 0.75, fontFamily: 'Kanit, sans-serif',
        }}>
          Material gratis
        </Typography>
        <Typography component="h1" sx={{
          fontFamily: 'Kanit, sans-serif', fontWeight: 800,
          fontSize: { xs: '2rem', md: '2.8rem' }, color: DARK, mb: 1,
        }}>
          Guía de sueño AU
        </Typography>
        <Typography sx={{ color: GRAY, mb: 4, maxWidth: 680, fontFamily: 'Kanit, sans-serif' }}>
          Todas las semanas me preguntan qué suplemento tomar para dormir mejor. La respuesta no es esa: el suplemento
          es lo último de la lista. Los 4 hábitos que sí mueven la aguja, en orden, y el stack de suplementos que uso yo.
        </Typography>

        {!unlocked ? (
          /* Gate — todo el material vive detrás del form */
          <Paper elevation={0} sx={{
            borderRadius: 3, border: `1px solid ${BLUE}`, bgcolor: '#fff',
            p: { xs: 3, md: 5 }, textAlign: 'center', mt: 1,
          }}>
            <Typography sx={{ fontWeight: 800, color: DARK, fontFamily: 'Kanit, sans-serif', fontSize: { xs: '1.4rem', md: '1.9rem' }, mb: 1 }}>
              Desbloquea la guía completa
            </Typography>
            <Typography sx={{ color: GRAY, fontFamily: 'Kanit, sans-serif', mb: 3, maxWidth: 560, mx: 'auto' }}>
              Déjanos tus datos y accede al instante a todo el material:
            </Typography>
            <Box sx={{ maxWidth: 500, mx: 'auto', textAlign: 'left', mb: 3.5 }}>
              {[
                'Los 4 hábitos en orden: estimulantes, última comida, ambiente y pantallas',
                'El pro tip de la sal que nadie te dice',
                'El stack de suplementos que uso yo, con dosis',
                'Checklist para aplicar esta misma noche + el PDF descargable',
              ].map((item) => (
                <Box key={item} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 1.25 }}>
                  <Box sx={{ color: BLUE, fontWeight: 800, fontFamily: 'Kanit, sans-serif', fontSize: 18, lineHeight: 1.35 }}>✓</Box>
                  <Typography sx={{ color: DARK, fontFamily: 'Kanit, sans-serif', fontSize: 15 }}>{item}</Typography>
                </Box>
              ))}
            </Box>
            <Button
              variant="contained" disableElevation onClick={handleUnlock}
              sx={{
                py: 1.5, px: 5, fontFamily: 'Kanit, sans-serif', fontWeight: 700, fontSize: 16,
                textTransform: 'none', bgcolor: BLUE, '&:hover': { bgcolor: BLUE_HOVER },
              }}
            >
              Ver la guía completa
            </Button>
            <Typography sx={{ color: GRAY, fontFamily: 'Kanit, sans-serif', fontSize: 12.5, mt: 2 }}>
              Gratis. Solo te pedimos tus datos para enviártelo y acompañarte en el proceso.
            </Typography>
          </Paper>
        ) : (
        <>
        <DescargaPDF
          url={PDF_URL}
          titulo="Descarga la guía de sueño en PDF"
          bajada="Los hábitos y el stack, para tenerlos a mano. Abajo está todo desarrollado."
        />
        {/* Disclaimer de suplementos */}
        <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', border: `1px solid ${BLUE}`, bgcolor: BLUE_SOFT, mb: 5 }}>
          <Box sx={{ bgcolor: BLUE, px: { xs: 2, md: 3 }, py: 1.25 }}>
            <Typography sx={{ fontWeight: 800, color: '#fff', fontFamily: 'Kanit, sans-serif', fontSize: { xs: 15, md: 17 } }}>
              El suplemento es lo último de la lista
            </Typography>
          </Box>
          <Box sx={{ px: { xs: 2, md: 3 }, py: 2 }}>
            <Typography sx={{ color: DARK, fontFamily: 'Kanit, sans-serif', fontSize: 14.5, mb: 1.5 }}>
              Dormir bien se construye con hábitos. Si los 4 hábitos de esta guía están firmes y todavía duermes mal,
              recién ahí tiene sentido sumar suplementos. El suplemento potencia los hábitos, no los reemplaza.
            </Typography>
            <Typography sx={{ color: BLUE_HOVER, fontFamily: 'Kanit, sans-serif', fontWeight: 700, fontSize: 15 }}>
              Parte por el hábito 1 y no saltes al final.
            </Typography>
          </Box>
        </Paper>

        {/* Los 4 hábitos */}
        <SectionTitle>Los 4 hábitos, en orden</SectionTitle>
        {HABITS.map((h) => <HabitBlock key={h.n} habit={h} />)}

        {/* Suplementos */}
        <SectionTitle>Recién ahora: suplementos</SectionTitle>
        <Typography sx={{ color: GRAY, mb: 2.5, maxWidth: 760, fontFamily: 'Kanit, sans-serif', fontSize: 14.5 }}>
          Lo que tomo yo antes de dormir, con dosis. Es mi protocolo personal, no una indicación médica: cada cuerpo
          es distinto, parte siempre por los hábitos y si tomas otros medicamentos o tienes dudas, revísalo con tu médico.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 3 }}>
          {SUPPLEMENTS.map((s) => <SupplementCard key={s.name} s={s} />)}
        </Box>

        {/* Dónde comprarlos: lista de MercadoLibre */}
        <Paper
          elevation={0}
          component="a"
          href={SUPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between',
            gap: 2, p: { xs: 2.5, md: 3 }, mb: 5, borderRadius: 3,
            border: `1px solid ${BLUE}`, bgcolor: BLUE_SOFT, textDecoration: 'none',
            transition: 'background-color .2s ease',
            '&:hover': { bgcolor: '#DDEAFF' },
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 800, color: BLUE_HOVER, fontFamily: 'Kanit, sans-serif', fontSize: { xs: 16, md: 18 }, mb: 0.5 }}>
              Dónde compro mis suplementos
            </Typography>
            <Typography sx={{ color: DARK, fontFamily: 'Kanit, sans-serif', fontSize: 14.5 }}>
              Encuentra estos y todos los suplementos que tomo y recomiendo, en un solo lugar.
            </Typography>
          </Box>
          <Button
            variant="contained" disableElevation component="span"
            sx={{
              flexShrink: 0, py: 1.2, px: 3, fontFamily: 'Kanit, sans-serif', fontWeight: 700,
              fontSize: 15, textTransform: 'none', bgcolor: BLUE, '&:hover': { bgcolor: BLUE_HOVER },
            }}
          >
            Ver la lista
          </Button>
        </Paper>

        {/* Checklist + PDF */}
        <SectionTitle>Checklist de esta noche</SectionTitle>
        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained" disableElevation component="a" href={PDF_URL} download
            sx={{
              py: 1.3, px: 3.5, fontFamily: 'Kanit, sans-serif', fontWeight: 700, fontSize: 15,
              textTransform: 'none', bgcolor: BLUE, '&:hover': { bgcolor: BLUE_HOVER },
            }}
          >
            Descargar PDF
          </Button>
        </Box>
        <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${BORDER}`, overflow: 'hidden', mb: 5 }}>
          {CHECKLIST.map((item, i) => (
            <Box key={item} sx={{
              display: 'flex', alignItems: 'center', gap: 1.5,
              px: 2.5, py: 1.5, bgcolor: i % 2 === 0 ? BLUE_SOFT : '#fff',
            }}>
              <Box sx={{ width: 16, height: 16, borderRadius: 1, border: `2px solid ${BLUE}`, flexShrink: 0 }} />
              <Typography sx={{ color: DARK, fontFamily: 'Kanit, sans-serif', fontSize: 14.5 }}>{item}</Typography>
            </Box>
          ))}
        </Paper>

        {/* CTA a coaching */}
        <Paper elevation={0} sx={{ mt: 5, p: { xs: 3, md: 4 }, borderRadius: 3, bgcolor: DARK }}>
          <Typography sx={{ color: '#fff', fontWeight: 800, fontFamily: 'Kanit, sans-serif', fontSize: { xs: 18, md: 22 }, mb: 0.75 }}>
            ¿Y si todo trabajara junto?
          </Typography>
          <Typography sx={{ color: '#CBD5E1', fontFamily: 'Kanit, sans-serif', fontSize: 14.5, mb: 2.5, maxWidth: 640 }}>
            Entrenamiento, nutrición y descanso dirigidos a un mismo objetivo, con seguimiento real semana a semana.
            Eso es exactamente lo que hacemos en AU Coaching.
          </Typography>
          <Button
            variant="contained" disableElevation onClick={() => navigate('/form')}
            sx={{
              py: 1.3, px: 3.5, fontFamily: 'Kanit, sans-serif', fontWeight: 700, fontSize: 15,
              textTransform: 'none', bgcolor: BLUE, '&:hover': { bgcolor: BLUE_HOVER },
            }}
          >
            Postula a AU Coaching
          </Button>
        </Paper>
        </>
        )}
      </Container>

      <VideoAccessForm
        open={showLeadForm}
        onClose={() => setShowLeadForm(false)}
        onSubmit={handleLeadSubmit}
        title="Desbloquea la guía de sueño"
        subtitle="Déjanos tus datos y te mostramos la guía completa al instante, con el PDF para descargar."
        submitLabel="Ver la guía completa"
      />
    </Box>
  );
};

export default SleepGuidePage;
